import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing emoji picker functionality
 * Handles emoji selection, insertion at cursor position, and lazy loading
 */
export const useEmojiPicker = ({ message, setMessage, isMobile, textAreaRef }) => {
    const [emojiOpen, setEmojiOpen] = useState(false);
    const emojiPreloadedRef = useRef(false);

    // Refs to track cursor position
    const selectionRef = useRef({ start: 0, end: 0 });
    const messageRef = useRef('');
    const isMobileRef = useRef(isMobile);

    useEffect(() => { messageRef.current = message || ''; }, [message]);
    useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);

    const updateSelectionFromEvent = useCallback((e) => {
        const start = e?.target?.selectionStart;
        const end = e?.target?.selectionEnd;
        if (typeof start === 'number' && typeof end === 'number') {
            selectionRef.current = { start, end };
        }
    }, []);

    const handleEmojiSelect = useCallback((emoji) => {
        const char = (emoji && (emoji.emoji || emoji.native)) || '';
        if (!char) return;

        const currentMessage = messageRef.current || '';
        const start = typeof selectionRef.current.start === 'number' ? selectionRef.current.start : currentMessage.length;
        const end = typeof selectionRef.current.end === 'number' ? selectionRef.current.end : currentMessage.length;
        const nextMessage = currentMessage.slice(0, start) + char + currentMessage.slice(end);
        setMessage(nextMessage);

        const nextPos = start + char.length;
        if (textAreaRef?.current) {
            const input = textAreaRef.current?.resizableTextArea?.textArea || textAreaRef.current?.input || textAreaRef.current;
            requestAnimationFrame(() => {
                try {
                    input.focus();
                    input.setSelectionRange(nextPos, nextPos);
                    selectionRef.current = { start: nextPos, end: nextPos };
                } catch (e) { }
            });
        }

        if (!isMobileRef.current) {
            setEmojiOpen(false);
        }
    }, [setMessage, textAreaRef]);

    const ensureEmojiPreloaded = useCallback(() => {
        if (emojiPreloadedRef.current) return;
        import(/* webpackPrefetch: true */ 'emoji-picker-react');
        emojiPreloadedRef.current = true;
    }, []);

    const handleEmojiButtonClick = useCallback(() => {
        ensureEmojiPreloaded();
        setEmojiOpen((prev) => !prev);
    }, [ensureEmojiPreloaded]);

    // Idle-time preloading to improve first open latency
    useEffect(() => {
        const idle = (cb) => (window.requestIdleCallback ? window.requestIdleCallback(cb) : setTimeout(cb, 1000));
        const id = idle(() => {
            ensureEmojiPreloaded();
        });
        return () => {
            if (window.cancelIdleCallback && typeof id === 'number') {
                try { window.cancelIdleCallback(id); } catch (e) { }
            } else {
                clearTimeout(id);
            }
        };
    }, [ensureEmojiPreloaded]);

    return {
        emojiOpen,
        setEmojiOpen,
        handleEmojiSelect,
        handleEmojiButtonClick,
        ensureEmojiPreloaded,
        updateSelectionFromEvent,
        selectionRef
    };
};
